/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo, useState } from 'react';
import {
	Typography,
	Skeleton,
	FormControl,
	Select,
	MenuItem,
	Stack,
	List,
	ListItem,
	ListItemIcon,
	Box,
} from '@mui/material';
import { useObjectContext } from '@/hooks/useObjectContext';
import { minimalInputTheme, minimalLabelTheme } from '@/app/theme';
import {
	useGetClientPoliciesQuery,
	useUpdateClientxMutation,
} from '@/features/clients/clientsApi';
import { useGetDealsByClientIdQuery } from '@/features/deals/dealsApi';
import { useCreateActivityMutation } from '@/features/engagements/engagementsApi';
import { ConvertProspectToClientDialog } from '@/components/dialogs/ConvertProspectToClient';
import { Warning } from '@mui/icons-material';
import { useListContactsQuery } from '@/features/contacts/contactsApi';

const TYPE_VALUES = [
	{ value: 0, label: 'Client' },
	{ value: 1, label: 'Prospect' },
];

const WarningIcon = () => (
	<ListItemIcon sx={{ minWidth: '42px', pl: '1em' }}>
		<Warning
			// color='warning'
			sx={{ fontSize: '1.2em' }}
		/>
	</ListItemIcon>
);

export const ClientTypeSelect = ({ title, minimal = true }) => {
	const [dialogOpen, setDialogOpen] = useState(false);

	const { clientRow, insight } = useObjectContext();

	const isProspect = clientRow?.isProspect;
	const insightId = clientRow?.insightId;
	const clientId = clientRow?.clientId;

	const { currentData: clientDeals, ...dealsQuery } = useGetDealsByClientIdQuery(clientId, {
		skip: !clientId,
	});

	const deals = useMemo(
		() =>
			(clientDeals ?? []).filter((d) => d.pipeline !== process.env.REACT_APP_PIPELINE_CLAIMS),
		[clientDeals],
	);
	const claims = useMemo(
		() =>
			(clientDeals ?? []).filter((d) => d.pipeline === process.env.REACT_APP_PIPELINE_CLAIMS),
		[clientDeals],
	);

	const { data: policies, isLoading: isLoadingPolicies } = useGetClientPoliciesQuery(
		{ insightId, clientId },
		{ skip: !insightId && !clientId },
	);

	const loadingClient = isLoadingPolicies || dealsQuery.isLoading;

	const [updateClientx, { isLoading }] = useUpdateClientxMutation();
	const [createActivity] = useCreateActivityMutation();

	const selectedValue = useMemo(() => (isProspect ? 1 : 0), [isProspect]);

	const isDisabled = useMemo(
		() =>
			(policies ?? []).length > 0 ||
			claims.length > 0 ||
			deals.some((d) => d.pipeline !== process.env.REACT_APP_PIPELINE_NEW_BUSINESS) ||
			deals.length > 1,
		[deals, claims, policies],
	);

	const { data: contacts } = useListContactsQuery(clientId, {
		skip: !clientId,
		selectFromResult: ({ data, ...rest }) => {
			const contacts = data ?? [];
			return {
				...rest,
				data: contacts,
			};
		},
	});
	const prospectContact = contacts?.find((c) => c.isMainContact);

	const address = insight?.AddressLine1;

	const newBusinessDeal = useMemo(() => {
		return deals.find((d) => d.pipeline === process.env.REACT_APP_PIPELINE_NEW_BUSINESS);
	}, [deals]);

	const hasDeal = newBusinessDeal && Object.keys(newBusinessDeal ?? {}).length > 0;
	const disableClient =
		hasDeal && (!prospectContact?.email || !prospectContact?.phone || !address);

	const updateClientType = async (event) => {
		const value = event.target.value;
		const isProspectType = value === TYPE_VALUES[1].value;

		if (!isProspectType && disableClient) {
			setDialogOpen(true);
			return;
		}

		await updateClientx({
			clientId,
			insight: {
				ClientTypeId: value,
			},
			firebase: {},
		}).unwrap();
		await createActivity({
			clientId: clientRow.id,
			activityType: isProspectType ? 'clientToProspect' : 'prospectToClient',
		}).unwrap();
	};

	return (
		<>
			<FormControl fullWidth>
				{title &&<Typography sx={minimalLabelTheme}>{title}</Typography>}
				{loadingClient || isLoading ? (
					<Skeleton animation='wave' width='100%' height='2em' />
				) : (
					<Select
						fullWidth
						value={selectedValue}
						onChange={updateClientType}
						disabled={isDisabled}
						variant={minimal ? 'standard' : 'outlined'}
						sx={{
							...(minimal && {
								...minimalInputTheme,
								'& .Mui-disabled': {
									backgroundColor: 'transparent',
								},
							}),
						}}
					>
						{TYPE_VALUES.map(({ value, label }, index) => (
							<MenuItem key={index} value={value}>
								{label}
							</MenuItem>
						))}
					</Select>
				)}
			</FormControl>
			{dialogOpen && (
				<ConvertProspectToClientDialog
					open={dialogOpen}
					handleClose={() => setDialogOpen(false)}
					{...(disableClient
						? {
								content: (
									<>
										<Stack>
											<Typography sx={{ color: 'red' }}>
												{'Error: Prospect is missing required fields:'}
											</Typography>
											<List dense>
												{!prospectContact?.email && (
													<ListItem disablePadding>
														<WarningIcon />
														Email address
													</ListItem>
												)}
												{!prospectContact?.phone && (
													<ListItem disablePadding>
														<WarningIcon />
														Phone number
													</ListItem>
												)}
												{!address && (
													<ListItem disablePadding>
														<WarningIcon />
														Address
													</ListItem>
												)}
											</List>
											<Box sx={{ pt: '1em' }}>
												<Typography>
													{
														'Please update these fields in order to convert prospect to client.'
													}
												</Typography>
											</Box>
										</Stack>
									</>
								),
							}
						: {
								additionalContent: `You will need to ${
									(deals ?? []).length > 0 ? 'complete' : 'add a'
								} new business in order to convert this prospect to a client.`,
							})}
					deal={newBusinessDeal}
				/>
			)}
		</>
	);
};
